import React, { useEffect, useState, useRef } from 'react';
import { Plus, Trash2, Paperclip, X, Send } from 'lucide-react';
import { api } from '../../lib/api';
import type { Assignment, User, AssignmentAttachment } from '../../types';
import { formatDateTime } from '../../lib/format';
import { useUpload } from '../../lib/useUpload';
import {
  Spinner, EmptyState, Modal, Alert, StatusBadge, ConfirmDialog, UploadProgress,
  inputClass, labelClass, primaryBtn, secondaryBtn,
} from '../../components/ui';

interface AssignmentForm {
  title: string;
  description: string;
  assigned_to: string;
  due_date: string;
}

const emptyForm: AssignmentForm = { title: '', description: '', assigned_to: '', due_date: '' };

function AttachmentsList({ label, attachments }: { label: string; attachments: AssignmentAttachment[] }) {
  if (!attachments.length) return null;
  return (
    <div className="mt-2">
      <p className="text-xs font-medium text-gray-500 mb-1">{label}</p>
      <div className="flex flex-wrap gap-2">
        {attachments.map((att) => (
          <a
            key={att.id}
            href={att.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <Paperclip className="h-3 w-3" />
            {att.filename}
          </a>
        ))}
      </div>
    </div>
  );
}

export default function Assignments() {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [staff, setStaff] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<AssignmentForm>(emptyForm);
  const [formFiles, setFormFiles] = useState<File[]>([]);
  const createUpload = useUpload();
  const respondUpload = useUpload();
  const [error, setError] = useState('');
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [respondingId, setRespondingId] = useState<number | null>(null);
  const [responseText, setResponseText] = useState('');
  const [responseFiles, setResponseFiles] = useState<File[]>([]);
  const [responseError, setResponseError] = useState('');
  const formFileRef = useRef<HTMLInputElement>(null);
  const responseFileRef = useRef<HTMLInputElement>(null);

  const load = () => {
    setLoading(true);
    api
      .get<Assignment[]>(`/assignments${statusFilter ? `?status=${statusFilter}` : ''}`)
      .then(setAssignments)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, [statusFilter]);
  useEffect(() => {
    api.get<User[]>('/auth/users?role=staff').then(setStaff).catch(() => {});
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    createUpload.reset();
    setError('');
    try {
      const formData = new FormData();
      formData.append('title', form.title);
      if (form.description) formData.append('description', form.description);
      formData.append('assigned_to', form.assigned_to);
      if (form.due_date) formData.append('due_date', form.due_date);
      for (const file of formFiles) {
        formData.append('assignor_attachments', file);
      }
      await createUpload.run('/assignments', formData);
      setModalOpen(false);
      setForm(emptyForm);
      setFormFiles([]);
      if (formFileRef.current) formFileRef.current.value = '';
      load();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const updateStatus = async (id: number, status: string, progress?: number) => {
    try {
      await api.patch(`/assignments/${id}/status`, { status, progress });
      load();
    } catch {
      // ignore
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/assignments/${id}`);
      setDeleteId(null);
      load();
    } catch {
      setDeleteId(null);
    }
  };

  const handleSubmitResponse = async (id: number) => {
    if (!responseText.trim() && responseFiles.length === 0) return;
    setResponseError('');
    respondUpload.reset();
    try {
      const formData = new FormData();
      if (responseText.trim()) formData.append('response_text', responseText.trim());
      for (const file of responseFiles) {
        formData.append('response_attachments', file);
      }
      await respondUpload.run(`/assignments/${id}/respond`, formData);
      setRespondingId(null);
      setResponseText('');
      setResponseFiles([]);
      if (responseFileRef.current) responseFileRef.current.value = '';
      load();
    } catch (err) {
      setResponseError((err as Error).message);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <h1 className="text-2xl font-bold">Assignments</h1>
        <button onClick={() => { setError(''); setFormFiles([]); setModalOpen(true); }} className={primaryBtn}>
          <Plus className="h-4 w-4" /> New Assignment
        </button>
      </div>

      <div className="mb-4">
        <select
          className="w-48 px-4 py-2 border border-gray-300 rounded-lg text-sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {loading ? (
        <Spinner label="Loading assignments..." />
      ) : assignments.length === 0 ? (
        <EmptyState message="No assignments found." />
      ) : (
        <div className="grid gap-4">
          {assignments.map((a) => (
            <div key={a.id} className="bg-white p-5 rounded-lg shadow-lg">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
                <div className="flex items-center space-x-3">
                  <h3 className="font-semibold">{a.title}</h3>
                  <StatusBadge status={a.status} />
                </div>
                <div className="flex items-center space-x-2 shrink-0">
                  <select
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs"
                    value={a.status}
                    onChange={(e) => updateStatus(a.id, e.target.value)}
                  >
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                  <button
                    onClick={() => updateStatus(a.id, 'completed', 100)}
                    disabled={a.status === 'completed'}
                    className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-medium hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Mark Done
                  </button>
                  <button
                    onClick={() => setDeleteId(a.id)}
                    className="p-2 rounded-lg hover:bg-red-50 text-red-500"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {a.description && <p className="text-sm text-gray-500 mb-2">{a.description}</p>}
              <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                <span>Assigned to: <strong>{a.assigned_to_name}</strong></span>
                <span>Due: {formatDateTime(a.due_date)}</span>
              </div>

              <AttachmentsList label="Attachments" attachments={a.assignor_attachments} />

              {a.response_text && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs font-medium text-blue-600 mb-1">Response from {a.assigned_to_name}</p>
                  <p className="text-sm text-gray-700">{a.response_text}</p>
                </div>
              )}
              <AttachmentsList label="Response attachments" attachments={a.response_attachments} />

              <div className="mt-3 max-w-md">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Progress</span>
                  <span>{a.progress}%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full" style={{ width: `${a.progress}%` }} />
                </div>
              </div>

              {respondingId === a.id ? (
                <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-3">
                  <p className="text-sm font-medium text-gray-700">Submit Response</p>
                  <textarea
                    className={inputClass}
                    rows={3}
                    placeholder="Describe your response..."
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                  />
                  <div>
                    <input
                      ref={responseFileRef}
                      type="file"
                      multiple
                      className="hidden"
                      accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv"
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        setResponseFiles((prev) => [...prev, ...files].slice(0, 15));
                        if (responseFileRef.current) responseFileRef.current.value = '';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => responseFileRef.current?.click()}
                      className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                    >
                      <Paperclip className="h-3 w-3" /> Attach files (max 15)
                    </button>
                  </div>
                  {responseFiles.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {responseFiles.map((file, idx) => (
                        <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded text-xs">
                          <Paperclip className="h-3 w-3" />
                          {file.name}
                          <button onClick={() => setResponseFiles((prev) => prev.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-600">
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <UploadProgress
                    percent={respondUpload.percent}
                    phase={respondUpload.phase}
                    error={respondUpload.error}
                    onCancel={respondUpload.cancel}
                  />
                  {responseError && <Alert>{responseError}</Alert>}
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setRespondingId(null); setResponseText(''); setResponseFiles([]); setResponseError(''); }}
                      className={secondaryBtn}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleSubmitResponse(a.id)}
                      disabled={respondUpload.busy || (!responseText.trim() && responseFiles.length === 0)}
                      className={primaryBtn}
                    >
                      <Send className="h-4 w-4" />
                      {respondUpload.phase === 'uploading'
                        ? `Uploading ${respondUpload.percent}%`
                        : respondUpload.phase === 'processing' ? 'Submitting...' : 'Submit'}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => {
                    setRespondingId(a.id);
                    setResponseText(a.response_text || '');
                    setResponseFiles([]);
                  }}
                  className="mt-3 px-4 py-2 border border-primary text-primary rounded-lg text-sm font-medium hover:bg-primary/5 inline-flex items-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  {a.response_text ? 'Edit Response' : 'Respond'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="New Assignment">
        {error && <Alert>{error}</Alert>}
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className={labelClass}>Title *</label>
            <input className={inputClass} required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div>
            <label className={labelClass}>Description</label>
            <textarea className={inputClass} rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div>
            <label className={labelClass}>Assign To *</label>
            <select className={inputClass} required value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}>
              <option value="">Select staff</option>
              {staff.map((s) => (
                <option key={s.id} value={s.id}>{s.name} ({s.email})</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Due Date & Time</label>
            <input type="datetime-local" className={inputClass} value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
          </div>
          <div>
            <label className={labelClass}>Attachments</label>
            <input
              ref={formFileRef}
              type="file"
              multiple
              className="hidden"
              accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                setFormFiles((prev) => [...prev, ...files].slice(0, 15));
                if (formFileRef.current) formFileRef.current.value = '';
              }}
            />
            <button
              type="button"
              onClick={() => formFileRef.current?.click()}
              className="text-sm text-primary hover:underline inline-flex items-center gap-1"
            >
              <Paperclip className="h-3 w-3" /> Attach files (max 15)
            </button>
            {formFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formFiles.map((file, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs">
                    <Paperclip className="h-3 w-3" />
                    {file.name}
                    <button type="button" onClick={() => setFormFiles((prev) => prev.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-600">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <UploadProgress
            percent={createUpload.percent}
            phase={createUpload.phase}
            error={createUpload.error}
            onCancel={createUpload.cancel}
          />
          <div className="flex justify-end space-x-3">
            <button type="button" onClick={() => setModalOpen(false)} className={secondaryBtn}>Cancel</button>
            <button type="submit" disabled={createUpload.busy} className={primaryBtn}>
              {createUpload.phase === 'uploading'
                ? `Uploading ${createUpload.percent}%`
                : createUpload.phase === 'processing' ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && handleDelete(deleteId)}
        title="Delete Assignment"
        message="Are you sure you want to delete this assignment?"
      />
    </div>
  );
}
